import { z } from 'zod'

/**
 * 제품 사양 계산 (2026-10-30 대표 결정 2: 결과 화면 사양 블록·시판 제품 100 g 당 가격 비교).
 * 순중량·1회 제공량·시판 제품 용량 글자를 같은 기준(g 또는 ml)으로 옮겨 나누기만 한다. 밀도 근거가 없으니
 * 질량과 부피는 서로 바꾸지 않는다 (formulation.ts 와 같은 원칙).
 */

/** 1회 제공량. 단위는 순중량과 같은 계열(g·ml)을 권하지만 tsp·tbsp 같은 라벨 단위도 받는다 */
export const servingSizeSchema = z.object({
  amount: z.number().positive().max(100_000),
  unit: z.string().trim().min(1).max(10),
})
export type ServingSize = z.infer<typeof servingSizeSchema>

export type AmountBasis = 'mass' | 'volume'
/** g(질량) 또는 ml(부피)로 옮긴 양 */
export interface BaseAmount {
  basis: AmountBasis
  amount: number
}

const UNIT_TO_BASE: Record<string, { basis: AmountBasis; factor: number }> = {
  mg: { basis: 'mass', factor: 0.001 },
  g: { basis: 'mass', factor: 1 },
  kg: { basis: 'mass', factor: 1000 },
  oz: { basis: 'mass', factor: 28.3495 },
  lb: { basis: 'mass', factor: 453.592 },
  lbs: { basis: 'mass', factor: 453.592 },
  ml: { basis: 'volume', factor: 1 },
  cc: { basis: 'volume', factor: 1 },
  l: { basis: 'volume', factor: 1000 },
  floz: { basis: 'volume', factor: 29.5735 },
  tsp: { basis: 'volume', factor: 5 },
  tbsp: { basis: 'volume', factor: 15 },
  그램: { basis: 'mass', factor: 1 },
  킬로그램: { basis: 'mass', factor: 1000 },
  밀리리터: { basis: 'volume', factor: 1 },
  리터: { basis: 'volume', factor: 1000 },
}

/** 단위 이름 정리: 대소문자·공백·마침표를 지운다 ("fl. oz" → "floz", "mL" → "ml") */
const unitKey = (unit: string) => unit.toLowerCase().replace(/[\s.]/g, '')

/** 양과 단위를 g 또는 ml 로. 모르는 단위(ea·개·컵)면 null */
export function toBaseAmount(amount: number, unit: string): BaseAmount | null {
  const hit = UNIT_TO_BASE[unitKey(unit)]
  if (!hit || !Number.isFinite(amount) || amount <= 0) return null
  return { basis: hit.basis, amount: amount * hit.factor }
}

/** 순중량 ÷ 1회 제공량 = 몇 회 분량 (내림). 계열이 다르거나 1회도 안 나오면 null */
export function servingsPerPack(
  netWeight: { amount: number; unit: string },
  serving: ServingSize | null | undefined,
): number | null {
  if (!serving) return null
  const net = toBaseAmount(netWeight.amount, netWeight.unit)
  const one = toBaseAmount(serving.amount, serving.unit)
  if (!net || !one || net.basis !== one.basis) return null
  const n = Math.floor(net.amount / one.amount + 1e-9)
  return n >= 1 ? n : null
}

// 숫자 + 단위. "fl oz" 를 "oz" 보다 먼저 본다. 단위 뒤에 영문자가 붙으면(예: "gal", "lbs" 의 일부) 다른 단위다
const AMOUNT_RE =
  /(\d{1,3}(?:,\d{3})+|\d+(?:[.,]\d+)?)\s*(fl\.?\s*oz|킬로그램|밀리리터|그램|리터|kg|mg|ml|lbs|lb|oz|cc|g|l)(?![a-z])/i
// "200 g x 3", "3 x 200 g" 같은 묶음 개수
const MULTI_AFTER_RE = /^\s*[x×*]\s*(\d{1,3})(?!\d)/i
const MULTI_BEFORE_RE = /(\d{1,3})\s*[x×*]\s*$/i

/**
 * 시판 제품 용량 글자(골든셋 packSize, 예: "250 g 병", "12 fl oz bottle", "200g x 3")를 g 또는 ml 로.
 * 첫 번째 '숫자+단위'만 읽고, 바로 앞뒤에 '× 개수'가 있으면 곱한다. 못 읽으면 null (비교 줄에서 빠진다)
 */
export function parsePackAmount(packSize: string): BaseAmount | null {
  const text = packSize.normalize('NFKC')
  const m = AMOUNT_RE.exec(text)
  if (!m || m.index === undefined) return null
  // "1,000 g" 은 천 단위 쉼표, "1,5 kg" 은 소수점 쉼표
  const digits = /^\d{1,3}(,\d{3})+$/.test(m[1]!)
    ? m[1]!.replace(/,/g, '')
    : m[1]!.replace(',', '.')
  const base = toBaseAmount(Number(digits), m[2]!)
  if (!base) return null
  const after = MULTI_AFTER_RE.exec(text.slice(m.index + m[0].length))
  const before = MULTI_BEFORE_RE.exec(text.slice(0, m.index))
  const count = Number(after?.[1] ?? before?.[1] ?? 1)
  return count >= 1 ? { basis: base.basis, amount: base.amount * count } : null
}

/** 100 g(또는 100 ml) 당 가격. 양이 없거나 가격이 0 이하면 null */
export function pricePer100(price: number | null | undefined, amount: BaseAmount | null) {
  if (price === null || price === undefined || !(price > 0) || !amount) return null
  return (price / amount.amount) * 100
}

export interface UnitPriceRow {
  id: string
  name: string
  /** 브랜드 · 용량 글자 그대로 */
  detail: string
  retailPrice: number
  per100: number
}
export interface UnitPriceComparison {
  basis: AmountBasis
  /** 내 제품 (판매가 ÷ 순중량) */
  mine: number
  rows: UnitPriceRow[]
  /** 시판 제품 100 단위 당 가격 범위 — 화면 표기는 '최소 ~ 최대' */
  range: { min: number; max: number } | null
}

/**
 * 내 제품과 '지금 팔리는 제품'(이미 찾아 둔 경쟁 제품)을 100 g(ml) 당 가격으로 맞춘다.
 * 내 순중량 계열과 같은 계열의 용량을 읽을 수 있고 통화가 같은 제품만 줄에 넣는다. 내 값을 못 구하면 null
 */
export function unitPriceComparison(
  mine: { price: number; netWeight: { amount: number; unit: string }; currency: string },
  items: ReadonlyArray<{
    id: string
    name: string
    brand: string
    packSize: string
    retailPrice: number | null
    currency: string
  }>,
): UnitPriceComparison | null {
  const own = toBaseAmount(mine.netWeight.amount, mine.netWeight.unit)
  const minePer100 = pricePer100(mine.price, own)
  if (!own || minePer100 === null) return null
  const rows = items.flatMap((item): UnitPriceRow[] => {
    if (item.currency !== mine.currency || item.retailPrice === null) return []
    const amount = parsePackAmount(item.packSize)
    const per100 = amount?.basis === own.basis ? pricePer100(item.retailPrice, amount) : null
    if (per100 === null) return []
    const detail = [item.brand, item.packSize].filter((s) => s.trim()).join(' · ')
    return [{ id: item.id, name: item.name, detail, retailPrice: item.retailPrice, per100 }]
  })
  const values = rows.map((r) => r.per100)
  return {
    basis: own.basis,
    mine: minePer100,
    rows,
    range: values.length ? { min: Math.min(...values), max: Math.max(...values) } : null,
  }
}

/**
 * 1회 제공량 칸을 모르는 옛 앱(1.0.7 이하)과 그 칸을 빠뜨린 AI 수정은 저장할 때 칸 없이 보낸다.
 * 저장돼 있던 값이 있으면 이어 붙여, 다른 곳을 고쳤다고 1회 제공량이 지워지지 않게 한다
 */
export function keepSavedServingSize<T extends { product: { servingSize?: ServingSize } }>(
  next: T,
  saved: { product: { servingSize?: ServingSize } } | null | undefined,
): T {
  const kept = saved?.product.servingSize
  if (next.product.servingSize || !kept) return next
  return { ...next, product: { ...next.product, servingSize: kept } }
}
